import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, Clock, Edit2, Trash2, Pin, Check, X, Sparkles, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminScheduledPosts() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [editForm, setEditForm] = useState({});
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/scheduled-posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data.posts);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès admin requis');
        navigate('/dashboard');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const initializeJourney = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/admin/scheduled-posts/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (post) => {
    setEditingPost(post.post_id);
    setEditForm({
      content_fr: post.content_fr,
      content_en: post.content_en,
      content_pt: post.content_pt,
      scheduled_date: post.scheduled_date?.slice(0, 16),
      is_pinned: post.is_pinned
    });
  };

  const saveEdit = async (postId) => {
    try {
      await axios.put(`${API}/api/admin/scheduled-posts/${postId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post mis à jour');
      setEditingPost(null);
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Supprimer ce post ?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/scheduled-posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post supprimé');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPhaseColor = (phase) => {
    const colors = {
      identity: 'bg-purple-500/20 text-purple-300',
      clarity: 'bg-blue-500/20 text-blue-300',
      action: 'bg-orange-500/20 text-orange-300',
      integration: 'bg-green-500/20 text-green-300',
      general: 'bg-gray-500/20 text-gray-300'
    };
    return colors[phase] || colors.general;
  };

  const getPhaseLabel = (phase) => {
    const labels = {
      identity: 'Identité',
      clarity: 'Clarté',
      action: 'Action',
      integration: 'Intégration',
      general: 'Général'
    };
    return labels[phase] || phase;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-serif text-white mb-2">Posts Programmés</h1>
            <p className="text-gray-400">Gérez le parcours guidé de 30 jours</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="border-gray-700 text-gray-300"
            >
              Retour
            </Button>
            {posts.length === 0 && (
              <Button 
                onClick={initializeJourney}
                className="bg-[#d4af37] text-black hover:bg-[#c4a030]"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Initialiser le Parcours 30 Jours
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-white">{posts.length}</p>
              <p className="text-gray-400 text-sm">Total Posts</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400">
                {posts.filter(p => p.is_published).length}
              </p>
              <p className="text-gray-400 text-sm">Publiés</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-400">
                {posts.filter(p => !p.is_published).length}
              </p>
              <p className="text-gray-400 text-sm">En Attente</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-400">
                {posts.filter(p => p.is_pinned).length}
              </p>
              <p className="text-gray-400 text-sm">Épinglés</p>
            </CardContent>
          </Card>
        </div>

        {/* Posts List */}
        <div className="space-y-4">
          {posts.map((post) => (
            <Card key={post.post_id} className={`bg-[#1a1a1a] border-gray-800 ${post.is_published ? 'opacity-60' : ''}`}>
              <CardContent className="p-6">
                {editingPost === post.post_id ? (
                  /* Edit Mode */
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm text-gray-400 mb-1 block">Français</label>
                        <Textarea
                          value={editForm.content_fr}
                          onChange={(e) => setEditForm({...editForm, content_fr: e.target.value})}
                          className="bg-[#0a0a0a] border-gray-700 text-white min-h-[150px]"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400 mb-1 block">English</label>
                        <Textarea
                          value={editForm.content_en}
                          onChange={(e) => setEditForm({...editForm, content_en: e.target.value})}
                          className="bg-[#0a0a0a] border-gray-700 text-white min-h-[150px]"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400 mb-1 block">Português</label>
                        <Textarea
                          value={editForm.content_pt}
                          onChange={(e) => setEditForm({...editForm, content_pt: e.target.value})}
                          className="bg-[#0a0a0a] border-gray-700 text-white min-h-[150px]"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div>
                        <label className="text-sm text-gray-400 mb-1 block">Date de publication</label>
                        <Input
                          type="datetime-local"
                          value={editForm.scheduled_date}
                          onChange={(e) => setEditForm({...editForm, scheduled_date: e.target.value})}
                          className="bg-[#0a0a0a] border-gray-700 text-white w-64"
                        />
                      </div>
                      <div className="flex items-center gap-2 mt-6">
                        <input
                          type="checkbox"
                          checked={editForm.is_pinned}
                          onChange={(e) => setEditForm({...editForm, is_pinned: e.target.checked})}
                          className="rounded"
                        />
                        <label className="text-sm text-gray-400">Épingler</label>
                      </div>
                      <div className="ml-auto flex gap-2 mt-6">
                        <Button
                          size="sm"
                          onClick={() => saveEdit(post.post_id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-1" /> Sauvegarder
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingPost(null)}
                          className="border-gray-600"
                        >
                          <X className="w-4 h-4 mr-1" /> Annuler
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {post.day && (
                          <Badge className="bg-[#d4af37]/20 text-[#d4af37]">
                            Jour {post.day}
                          </Badge>
                        )}
                        <Badge className={getPhaseColor(post.phase)}>
                          {getPhaseLabel(post.phase)}
                        </Badge>
                        {post.is_pinned && (
                          <Badge className="bg-red-500/20 text-red-300">
                            <Pin className="w-3 h-3 mr-1" /> Épinglé
                          </Badge>
                        )}
                        {post.is_published && (
                          <Badge className="bg-green-500/20 text-green-300">
                            Publié
                          </Badge>
                        )}
                      </div>
                      <p className="text-white whitespace-pre-wrap">{post.content_fr}</p>
                      <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(post.scheduled_date)}
                        </span>
                        {post.published_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Publié le {formatDate(post.published_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    {!post.is_published && (
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEdit(post)}
                          className="border-gray-700"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deletePost(post.post_id)}
                          className="border-red-800 text-red-400 hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {posts.length === 0 && (
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardContent className="p-12 text-center">
                <Sparkles className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">Aucun post programmé</h3>
                <p className="text-gray-400 mb-6">
                  Cliquez sur "Initialiser le Parcours 30 Jours" pour créer automatiquement
                  les posts du parcours guidé.
                </p>
                <Button 
                  onClick={initializeJourney}
                  className="bg-[#d4af37] text-black hover:bg-[#c4a030]"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Initialiser le Parcours
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
